/** "Næst á dagskrá" hero card on the home screen. */
import { CalendarDays, ChevronRight, MapPin, MessageCircle } from 'lucide-react';
import { Link } from 'react-router';
import type { EventDTO } from '../../../shared/dto/events';
import { useRequiredMe } from '../../auth/AuthProvider';
import { Card, cx } from '../../components/ui';
import { useI18n, useT } from '../../i18n';
import { dayHeading, formatTimeRange, todayStr } from '../../lib/dates';
import { SetlistOfflinePrompt } from '../../offline/SetlistPrompt';
import { CheckinControl, RsvpButtons, RsvpChip, useParticipation } from './participation';
import { canParticipate, KindBadge, kindKey, mapsUrl, OK_FILL } from './shared';

export function CommentsBadge({ ev, className }: { ev: EventDTO; className?: string }) {
  const t = useT();
  return (
    <span className={cx('inline-flex items-center gap-1.5', className)}>
      <MessageCircle className="size-5" aria-hidden />
      <span>{t('calendar.event.comments', { count: ev.commentCount })}</span>
      {ev.unreadCount > 0 ? (
        <span className="rounded-full bg-[var(--danger)] px-2 py-0.5 text-[13px] font-bold text-white">
          {t('calendar.event.unread', { count: ev.unreadCount })}
        </span>
      ) : null}
    </span>
  );
}

export function NextUpCard({ ev, now }: { ev: EventDTO; now: number }) {
  const t = useT();
  const { loc } = useI18n();
  const me = useRequiredMe();
  const { setRsvp, checkin } = useParticipation(ev);
  const today = todayStr(now);
  const ongoing = ev.startsAt <= now && now < ev.endsAt;
  const mine = canParticipate(ev, me.section);
  const cancelled = ev.status === 'cancelled';
  // RSVP is open until the start: until then an unanswered member is asked on the card itself.
  const unanswered = mine && !cancelled && now < ev.startsAt && ev.my.effectiveRsvp === null;

  return (
    <Card as="section" className="overflow-hidden">
      <div className="h-1.5 bg-[var(--accent)]" aria-hidden />
      <div className="flex flex-col gap-4 p-5 md:p-6">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-[14px] font-bold tracking-wider text-[var(--accent)] uppercase">{t('calendar.next_up')}</h2>
          {ongoing ? (
            <span className={cx('rounded-full px-3 py-1 text-[13px] font-bold text-white', OK_FILL)}>{t('calendar.event.ongoing')}</span>
          ) : cancelled ? (
            <span className="rounded-full bg-[var(--danger)] px-3 py-1 text-[13px] font-bold text-white">{t('calendar.event.cancelled')}</span>
          ) : unanswered ? (
            <RsvpChip ev={ev} />
          ) : null}
        </div>

        <Link to={`/events/${ev.id}`} className="flex items-start gap-4 rounded-2xl">
          <KindBadge kind={ev.kind} cancelled={cancelled} size="lg" />
          <div className="min-w-0 flex-1">
            <p className={cx('text-[24px] leading-tight font-bold md:text-[28px]', cancelled && 'text-[var(--muted)] line-through')}>{ev.title}</p>
            {ev.title !== t(kindKey(ev.kind)) ? <p className="mt-1 text-[15px] font-semibold text-[var(--muted)]">{t(kindKey(ev.kind))}</p> : null}
          </div>
          <ChevronRight className="mt-3 size-6 shrink-0 text-[var(--muted)]" aria-hidden />
        </Link>

        <div className="flex flex-col gap-2 text-[17px]">
          <p className="flex items-center gap-2.5">
            <CalendarDays className="size-5 shrink-0 text-[var(--muted)]" aria-hidden />
            <span className="font-semibold">{dayHeading(todayStr(ev.startsAt), today, loc)}</span>
            <span className="text-[var(--ink-2)] tabular-nums">{formatTimeRange(ev.startsAt, ev.endsAt, loc)}</span>
          </p>
          {ev.location ? (
            <a
              href={mapsUrl(ev.location)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-h-11 items-center gap-2.5 text-[var(--ink-2)]"
              aria-label={`${ev.location} – ${t('calendar.event.open_map')}`}
            >
              <MapPin className="size-5 shrink-0 text-[var(--muted)]" aria-hidden />
              <span className="underline decoration-[var(--line)] underline-offset-4">{ev.location}</span>
            </a>
          ) : null}
        </div>

        {mine && !cancelled ? (
          <>
            {/* RSVP closes when the event starts; from then on only check-in matters. */}
            {now < ev.startsAt ? <RsvpButtons ev={ev} onPick={(r) => void setRsvp(r)} /> : null}
            <CheckinControl ev={ev} now={now} onCheckin={() => void checkin()} />
          </>
        ) : null}

        {ev.setlistId && !cancelled ? <SetlistOfflinePrompt setlistId={ev.setlistId} /> : null}

        <div className="flex flex-wrap items-center gap-2 border-t border-[var(--line)] pt-3">
          {/* The songs to practise: the home screen's practice card (practice/PracticeMenu). */}
          <Link to={`/events/${ev.id}#spjall`} className="inline-flex min-h-11 items-center rounded-xl px-3 font-semibold text-[var(--ink-2)] hover:bg-[var(--accent-soft)]">
            <CommentsBadge ev={ev} />
          </Link>
          <Link
            to={`/events/${ev.id}`}
            className="ml-auto inline-flex min-h-11 items-center gap-1 rounded-xl px-3 font-semibold text-[var(--accent)] hover:bg-[var(--accent-soft)]"
          >
            {t('calendar.event.details')}
            <ChevronRight className="size-5" aria-hidden />
          </Link>
        </div>
      </div>
    </Card>
  );
}
